import React, { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { Box, Paper, Typography, Stack, IconButton, Tabs, Tab, useTheme, useMediaQuery, Select, MenuItem, FormControl, InputLabel, alpha } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import {
  Cell, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, ReferenceLine, ReferenceArea,
  ScatterChart, Scatter, ZAxis,
  AreaChart, Area, Legend, LabelList,
} from "recharts";
import { toPng } from "html-to-image";
import { useDraftData } from "../context/DraftDataContext";
import { useAdp } from "../context/AdpContext";
import { useMarketAdp } from "../helpers/useMarketAdp";
import { groupDataByPlayer, computePlayerCLV, getTeamCLV, normalizePlayerName } from "../helpers/playerHelpers";
import { teamColorFg, teamColorBg } from "../helpers/colorHelpers";
import {
  getTeamsTableFromCsv,
  getUniquePlayersByTeam,
  getStackedTeamPositionChartData,
  getLineChartDataForPositionByRound,
  getDraftSlotHistogram,
  getTeamStructureHistogram,
  getTeamPairingFrequencies,
} from "../helpers/chartHelpers";
import { POSITION_COLORS, TEAM_COLORS } from "../constants";
import StatCard from "../components/StatCard";
import HeroEmptyState from "../components/HeroEmptyState";
import { AXIS_TICK, CHART_TOOLTIP_SX, CHART_EXPORT_BG } from "../components/charts/chartTheme";
import { useStatusColors } from "../helpers/statusColors";
import { semantic, surfaceTokens } from "../config/theme";
import { slotBandClv, buildChangeFeed, ChangeFeed, VisitSnapshot } from "../helpers/portfolioInsights";

const VISIT_SNAPSHOT_KEY = "rosteros:visitSnapshot";

const EXPECTED_EXPOSURE = 100 / 12; // 8.33% in a 12-person draft

// ── Widget card ───────────────────────────────────────────────────

function Widget({ title, description, children, accent, sx, headerRight }: {
  title: string;
  description?: string;
  children: React.ReactNode;
  accent?: string;
  sx?: object;
  headerRight?: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{
      borderRadius: 2,
      borderTop: accent ? `3px solid ${accent}` : undefined,
      ...sx,
    }}>
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: description ? 0.5 : 1.5, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
          {headerRight}
        </Box>
        {description && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>{description}</Typography>
        )}
        {children}
      </Box>
    </Paper>
  );
}

// ── Dark tooltip ──────────────────────────────────────────────────

const DarkTooltip = ({ active, payload, label, formatter }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ ...CHART_TOOLTIP_SX, minWidth: 120 }}>
      {label !== undefined && <Box sx={{ fontWeight: 700, mb: 0.75, color: "#ccc" }}>{label}</Box>}
      {payload.map((entry: any, i: number) => (
        <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: entry.color }} />
            <span>{entry.name}</span>
          </Box>
          <span style={{ fontWeight: 600 }}>
            {formatter
              ? (() => { try { return formatter(entry.value, entry.name, entry); } catch { return entry.value; } })()
              : entry.value}
          </span>
        </Box>
      ))}
    </Box>
  );
};

// ── Exposure Heatmap ──────────────────────────────────────────────

function ExposureHeatmap({ players, onNavigate }: {
  players: Array<{ name: string; exposure: number; position: string; team: string }>;
  onNavigate: (name: string) => void;
}) {
  const top = players.slice(0, 15);
  const maxExp = Math.max(...top.map(p => p.exposure), 1);
  const expectedPct = (EXPECTED_EXPOSURE / maxExp) * 100;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {top.map(p => {
        const intensity = p.exposure / maxExp;
        const posColor = POSITION_COLORS[p.position] || "#888";
        return (
          <Box
            key={p.name + p.position}
            onClick={() => onNavigate(p.name)}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer",
              borderRadius: 1, px: 1, py: 0.5,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box sx={{
              fontSize: 9, fontWeight: 700, color: "#fff", bgcolor: posColor,
              borderRadius: 0.75, px: 0.75, py: 0.2, minWidth: 28, textAlign: "center",
              flexShrink: 0,
            }}>{p.position}</Box>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.75, overflow: "hidden" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 500,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 1 }}>
                {p.name}
              </Typography>
              {p.team && (
                <Typography sx={{ fontSize: 10, color: "text.secondary", whiteSpace: "nowrap", flexShrink: 0 }}>
                  {p.team}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1.5, minWidth: 60, height: 8, bgcolor: "action.hover", borderRadius: 1, overflow: "visible", position: "relative" }}>
              <Box sx={{
                width: `${intensity * 100}%`, height: "100%", borderRadius: 1,
                bgcolor: posColor, opacity: 0.3 + intensity * 0.7,
              }} />
              <Box sx={{
                position: "absolute", top: -2, bottom: -2, left: `${expectedPct}%`,
                width: "1.5px", bgcolor: "text.disabled", opacity: 0.5,
              }} />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, minWidth: 38, textAlign: "right",
              color: posColor, flexShrink: 0 }}>
              {p.exposure.toFixed(0)}%
            </Typography>
          </Box>
        );
      })}
      <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.5, pl: 1 }}>
        vertical mark = 8.3% expected · click any row to filter
      </Typography>
    </Box>
  );
}

// ── ADP Movers ────────────────────────────────────────────────────

function AdpMovers({ adpHistory, playerNames, exposureByName, onSelect }: {
  adpHistory: any[];
  playerNames: Set<string>;
  exposureByName: Map<string, number>;
  onSelect?: (name: string) => void;
}) {
  const sc = useStatusColors();
  const movers = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ago7Date = new Date();
    ago7Date.setDate(ago7Date.getDate() - 7);
    const ago7 = ago7Date.toISOString().slice(0, 10);

    return adpHistory
      .filter(p => playerNames.has(p.name))
      .map(p => {
        const hist = p.history as Array<{ date: string; adp: number }>;
        let cur: number | null = null, old7: number | null = null;
        for (const h of hist) {
          if (h.date <= today) cur = h.adp;
          if (h.date <= ago7) old7 = h.adp;
        }
        const delta = cur !== null && old7 !== null ? old7 - cur : null;
        // Portfolio impact = how far it moved x how much you own it. A small
        // move you're heavy in outranks a big move you own once.
        const exposure = exposureByName.get(p.name) ?? 0;
        const impact = delta !== null ? (delta * exposure) / 100 : null;
        return { ...p, delta, exposure, impact };
      })
      .filter(p => p.delta !== null);
  }, [adpHistory, playerNames, exposureByName]);

  // Sort by impact (signed), not raw delta.
  const risers = [...movers].sort((a, b) => (b.impact ?? 0) - (a.impact ?? 0)).slice(0, 15);
  const fallers = [...movers].sort((a, b) => (a.impact ?? 0) - (b.impact ?? 0)).slice(0, 15);
  const maxImpact = Math.max(0.1, ...movers.map(p => Math.abs(p.impact ?? 0)));

  function MoverRow(
    { name, position, team, delta, exposure, impact, anchor }:
    { name: string; position: string; team: string; delta: number; exposure: number; impact: number; anchor: "left" | "right" },
  ) {
    const isPos = delta > 0;
    const tone = isPos ? sc.good : sc.bad;
    const posColor = POSITION_COLORS[position] || "#888";
    const barPct = Math.min((Math.abs(impact) / maxImpact) * 100, 100);
    return (
      <Box
        onClick={onSelect ? () => onSelect(name) : undefined}
        sx={{
          display: "flex", alignItems: "center", gap: 1, py: 0.5,
          ...(onSelect && { cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }),
        }}
      >
        <Box sx={{ fontSize: 9, fontWeight: 700, color: "#fff", bgcolor: posColor,
          borderRadius: 0.75, px: 0.75, py: 0.25, minWidth: 28, textAlign: "center", flexShrink: 0 }}>
          {position}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis", flexShrink: 1 }}>
            {name}
          </Typography>
          {team && <Typography sx={{ fontSize: 11, color: "text.secondary", flexShrink: 0 }}>{team}</Typography>}
          <Typography sx={{ fontSize: 11, color: "text.disabled", flexShrink: 0 }}>· {Math.round(exposure)}% owned</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: tone, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
          {isPos ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}
        </Typography>
        {/* Impact bar — grows from the outer edge so the two columns mirror. */}
        <Box sx={{ width: 44, flexShrink: 0, height: 6, borderRadius: 3, bgcolor: "action.hover", overflow: "hidden" }}>
          <Box sx={{ height: "100%", width: `${barPct}%`, bgcolor: tone, borderRadius: 3, ml: anchor === "right" ? "auto" : 0 }} />
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: tone, flexShrink: 0, width: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {impact >= 0 ? "+" : "−"}{Math.abs(impact).toFixed(1)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: sc.good, mb: 0.75 }}>▲ Risers · by impact</Typography>
        {risers.length > 0 ? risers.map(p => (
          <MoverRow key={p.id} name={p.name} position={p.position} team={p.team ?? ""} delta={p.delta!} exposure={p.exposure} impact={p.impact!} anchor="left" />
        )) : <Typography sx={{ fontSize: 12, color: "text.disabled" }}>No data</Typography>}
      </Box>
      <Box sx={(theme) => ({
        borderTop: { xs: `1px solid ${theme.palette.divider}`, sm: "none" },
        borderLeft: { xs: "none", sm: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` },
        pt: { xs: 1.25, sm: 0 }, pl: { xs: 0, sm: 2 },
      })}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: sc.bad, mb: 0.75 }}>▼ Fallers · by impact</Typography>
        {fallers.length > 0 ? fallers.map(p => (
          <MoverRow key={p.id} name={p.name} position={p.position} team={p.team ?? ""} delta={p.delta!} exposure={p.exposure} impact={p.impact!} anchor="right" />
        )) : <Typography sx={{ fontSize: 12, color: "text.disabled" }}>No data</Typography>}
      </Box>
    </Box>
  );
}

// ── Exposure vs. Value ────────────────────────────────────────────

const EdgeTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{ ...CHART_TOOLTIP_SX, minWidth: 160 }}>
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>{d.name}</Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.25 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: POSITION_COLORS[d.position] || "#888", flexShrink: 0 }} />
        <span>{d.position}</span>
      </Box>
      <Box>Exposure: <b>{d.exposure.toFixed(1)}%</b></Box>
      <Box>CLV: <b>{d.clv > 0 ? "+" : ""}{d.clv.toFixed(1)}</b></Box>
      <Box>Picks: <b>{d.count}</b></Box>
    </Box>
  );
};

function ExposureVsValue({ players, onNavigate }: {
  players: Array<{ name: string; position: string; exposure: number; clv: number | null; pickNumbers: number[] }>;
  onNavigate: (name?: string) => void;
}) {
  const sc = useStatusColors();
  const data = players
    .filter(p => typeof p.clv === "number" && isFinite(p.clv as number) && Math.abs(p.clv as number) <= 40)
    .map(p => ({ name: p.name, position: p.position, exposure: p.exposure, clv: p.clv as number, count: p.pickNumbers.length }));

  const maxExp = data.length > 0 ? Math.max(...data.map(d => d.exposure), 1) : 30;
  const midExp = EXPECTED_EXPOSURE;
  const rawMin = data.length > 0 ? Math.min(...data.map(d => d.clv)) : -10;
  const rawMax = data.length > 0 ? Math.max(...data.map(d => d.clv)) : 10;
  const yExtentRaw = Math.max(Math.abs(rawMin), Math.abs(rawMax), 5) * 1.15;
  const yExtent = Math.ceil(yExtentRaw);
  const yMin = -yExtent;
  const yMax = yExtent;

  const tickStep = yExtent <= 5 ? 1 : yExtent <= 10 ? 2 : yExtent <= 20 ? 5 : 10;
  const yTicks: number[] = [0];
  for (let v = tickStep; v <= yExtent; v += tickStep) {
    yTicks.push(v);
    yTicks.push(-v);
  }
  yTicks.sort((a, b) => a - b);

  const qCounts = data.reduce((acc, d) => {
    const top = d.clv >= 0;
    const right = d.exposure >= midExp;
    const key = `${top ? "T" : "B"}_${right ? "R" : "L"}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const qTotal = data.length || 1;
  const qPct = (key: string) => Math.round((qCounts[key] || 0) / qTotal * 100);

  // The two high-exposure quadrants carry the insight: top-right = your edge
  // (green), bottom-right = concentrated risk (amber — a category, not a
  // negative number, so never red). The low-exposure halves are neutral.
  const quadrants = [
    { label: "Low own · +CLV",        pctKey: "T_L", color: semantic.neutral },
    { label: "Your edge · high +CLV", pctKey: "T_R", color: sc.good },
    { label: "Low own · −CLV",        pctKey: "B_L", color: semantic.neutral },
    { label: "Concentrated risk",     pctKey: "B_R", color: sc.warn },
  ];
  return (
    <>
      <Box sx={{ position: "relative", height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 10, right: 16, bottom: 24, left: 42 }}>
            <XAxis
              type="number" dataKey="exposure" name="Exposure"
              domain={[0, maxExp * 1.05]}
              tick={{ fontSize: 10, fill: semantic.neutral }} axisLine={false} tickLine={false}
              tickFormatter={v => `${Math.round(v)}%`}
              label={{ value: "Exposure %", position: "insideBottomRight", offset: -4, fontSize: 10, fill: semantic.neutral }}
            />
            <YAxis
              type="number" dataKey="clv" name="CLV"
              domain={[yMin, yMax]}
              ticks={yTicks}
              allowDecimals={false}
              tick={{ fontSize: 10, fill: semantic.neutral }} axisLine={false} tickLine={false}
              tickFormatter={v => v > 0 ? `+${v}` : `${v}`}
              label={{ value: "CLV (picks)", angle: -90, position: "insideLeft", offset: 14, fontSize: 10, fill: semantic.neutral }}
            />
            <ZAxis type="number" dataKey="count" range={[40, 300]} name="Picks" />
            {/* Quadrant tints — drawn under the dots. */}
            <ReferenceArea x1={midExp} x2={maxExp * 1.05} y1={0} y2={yMax} fill={sc.good} fillOpacity={0.06}
              label={{ value: "YOUR EDGE", position: "insideTopRight", fontSize: 9, fontWeight: 700, fill: sc.good }} />
            <ReferenceArea x1={midExp} x2={maxExp * 1.05} y1={yMin} y2={0} fill={sc.warn} fillOpacity={0.07}
              label={{ value: "CONCENTRATED RISK", position: "insideBottomRight", fontSize: 9, fontWeight: 700, fill: sc.warn }} />
            <RechartsTooltip content={<EdgeTooltip />} cursor={{ strokeDasharray: "3 3" }} />
            <ReferenceLine y={0} stroke={semantic.neutral} strokeDasharray="4 4" />
            <ReferenceLine x={midExp} stroke={semantic.neutral} strokeDasharray="4 4" />
            <Scatter
              data={data}
              shape={(props: any) => {
                const { cx, cy, payload } = props;
                const fill = POSITION_COLORS[payload.position] || "#888";
                const r = props.zAxis?.value ? Math.sqrt(props.zAxis.value) * 1.5 : 6;
                return (
                  <circle
                    cx={cx} cy={cy} r={r}
                    fill={fill} fillOpacity={0.8} stroke="#fff" strokeWidth={1}
                    style={{ cursor: "pointer" }}
                    onClick={() => onNavigate(payload.name)}
                  />
                );
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, mt: 1, maxWidth: 480, mx: "auto", width: "100%" }}>
        {quadrants.map(q => (
          <Box key={q.label} sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            px: 0.75, py: 0.5, borderRadius: 1,
            bgcolor: alpha(q.color, 0.12), border: "1px solid", borderColor: alpha(q.color, 0.3),
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: q.color, lineHeight: 1.3, textAlign: "center" }}>
              {q.label}
            </Typography>
            <Typography sx={{ fontSize: 9, color: "text.secondary", lineHeight: 1.2, textAlign: "center" }}>
              {qPct(q.pctKey)}% of portfolio
            </Typography>
          </Box>
        ))}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
        {(["QB", "RB", "WR", "TE"] as const).map(pos => (
          <Box key={pos} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: POSITION_COLORS[pos] }} />
            <Typography sx={{ fontSize: 10 }}>{pos}</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: 10, color: "text.disabled", ml: 0.5 }}>· dot size = pick count</Typography>
      </Stack>
    </>
  );
}

// ── CLV Distribution ──────────────────────────────────────────────

function ClvDistribution({ pickClvValues, clvBuckets }: {
  pickClvValues: number[];
  clvBuckets: Array<{ clv: number; count: number }>;
}) {
  const sc = useStatusColors();
  const pctPositive = pickClvValues.length > 0
    ? Math.round((pickClvValues.filter(v => v > 0).length / pickClvValues.length) * 100)
    : 0;
  const netClv = pickClvValues.reduce((a, b) => a + b, 0);
  const meanClv = pickClvValues.length > 0 ? netClv / pickClvValues.length : 0;
  // Nearest existing bucket to the mean, so the reference line sits on a real
  // category (the XAxis is categorical).
  const meanBucket = clvBuckets.length > 0
    ? clvBuckets.reduce((best, b) => (Math.abs(b.clv - meanClv) < Math.abs(best.clv - meanClv) ? b : best)).clv
    : 0;

  return (
    <>
      {pickClvValues.length > 0 && (
        <Box sx={{
          mb: 1.5, p: 1.25, borderRadius: 1.5,
          bgcolor: netClv >= 0 ? "rgba(11,122,52,0.08)" : "rgba(220,38,38,0.08)",
          border: "1px solid",
          borderColor: netClv >= 0 ? "rgba(11,122,52,0.2)" : "rgba(220,38,38,0.2)",
        }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: netClv >= 0 ? sc.good : sc.bad, fontVariantNumeric: "tabular-nums" }}>
              {netClv >= 0 ? "+" : ""}{netClv.toFixed(1)} net
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              <b style={{ color: pctPositive >= 50 ? sc.good : sc.bad }}>{pctPositive}%</b> of {pickClvValues.length.toLocaleString()} picks beat the market
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
            Each bar is one pick. The dashed line is your average edge; the solid line is the market (0).
          </Typography>
        </Box>
      )}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={clvBuckets} barCategoryGap="15%">
          <XAxis dataKey="clv" tick={{ fontSize: 11, fill: semantic.neutral }} axisLine={false} tickLine={false}
            tickFormatter={v => v > 0 ? `+${v}` : `${v}`} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: semantic.neutral }} axisLine={false} tickLine={false} />
          <RechartsTooltip content={<DarkTooltip />} />
          <ReferenceLine x={0} stroke={semantic.neutral} strokeWidth={1.4} />
          {pickClvValues.length > 0 && meanBucket !== 0 && (
            <ReferenceLine x={meanBucket} stroke={sc.good} strokeDasharray="4 3"
              label={{ value: `you avg ${meanClv >= 0 ? "+" : ""}${meanClv.toFixed(1)}`, fill: sc.good, fontSize: 10, position: "top" }} />
          )}
          <Bar dataKey="count" name="Picks" radius={[4, 4, 0, 0]}>
            {clvBuckets.map(b => (
              <Cell key={b.clv} fill={b.clv >= 0 ? sc.good : sc.bad} opacity={0.75} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
}

// ── Unique Players tooltip ────────────────────────────────────────

const UniquePlayersTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  if (!data.players) return null;
  const sortedPlayers = [...(data.players as any[])].sort((a: any, b: any) => {
    if (b.count !== a.count) return b.count - a.count;
    const aAdp = typeof a.avgAdp === "number" ? a.avgAdp : Number.POSITIVE_INFINITY;
    const bAdp = typeof b.avgAdp === "number" ? b.avgAdp : Number.POSITIVE_INFINITY;
    return aAdp !== bAdp ? aAdp - bAdp : String(a.name).localeCompare(String(b.name));
  });
  return (
    <Box sx={{ ...CHART_TOOLTIP_SX, minWidth: undefined, maxWidth: 240 }}>
      <Box sx={{ fontWeight: 700, mb: 0.75 }}>{data.team}</Box>
      <Box sx={{ color: "#aaa", mb: 0.5 }}>{data.uniquePlayers} unique players</Box>
      {sortedPlayers.slice(0, 6).map((p: any) => (
        <Box key={p.name} sx={{ color: "#ccc", fontSize: 11 }}>
          {p.name} <span style={{ color: "#888" }}>({p.count}x)</span>
        </Box>
      ))}
      {sortedPlayers.length > 6 && (
        <Box sx={{ color: "#888", fontSize: 11 }}>+{sortedPlayers.length - 6} more</Box>
      )}
      <Box sx={{ color: "#888", mt: 0.75, fontSize: 11 }}>Click to filter players by team</Box>
    </Box>
  );
};

// ── Team Pairing Widget ───────────────────────────────────────────

function TeamChip({ team }: { team: string }) {
  const { palette } = useTheme();
  const raw = TEAM_COLORS[team] || "#888";
  const color = teamColorFg(raw, palette.mode === "dark");
  return (
    <Box sx={{
      px: 0.75, py: 0.25, borderRadius: 0.75,
      bgcolor: color + "22",
      border: `1px solid ${color}66`,
      color,
      fontSize: 11, fontWeight: 700, lineHeight: 1.4, flexShrink: 0,
    }}>{team}</Box>
  );
}

function PairingRow({ team1, team2, count, rate, totalRosters }: {
  team1: string; team2: string; count: number; rate: number; totalRosters: number;
}) {
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";
  const barColor = teamColorBg(TEAM_COLORS[team1] || "#888", isDark);
  const pct = (rate * 100).toFixed(1);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0, width: 84 }}>
        <TeamChip team={team1} />
        <TeamChip team={team2} />
      </Box>
      <Box sx={{ flex: 1, bgcolor: "action.hover", borderRadius: 1, height: 6, overflow: "hidden" }}>
        <Box sx={{
          width: `${rate * 100}%`, height: "100%",
          bgcolor: barColor, borderRadius: 1, minWidth: rate > 0 ? 2 : 0,
        }} />
      </Box>
      <Box sx={{ flexShrink: 0, textAlign: "right", minWidth: 80, fontSize: 12 }}>
        <span style={{ fontWeight: 600 }}>{pct}%</span>
        <span style={{ color: semantic.neutral, marginLeft: 4 }}>({count}/{totalRosters})</span>
      </Box>
    </Box>
  );
}

function TeamPairingWidget({ pairings, myTeams, totalRosters }: {
  pairings: import("../helpers/chartHelpers").TeamPairing[];
  myTeams: string[];
  totalRosters: number;
}) {
  const [selectedTeam, setSelectedTeam] = useState("");

  const { top10, bottom10, filtered } = useMemo(() => {
    if (selectedTeam) {
      const f = pairings
        .filter(p => p.team1 === selectedTeam || p.team2 === selectedTeam)
        .sort((a, b) => b.rate - a.rate);
      return { top10: [], bottom10: [], filtered: f };
    }
    const myTeamSet = new Set(myTeams);
    const bothDrafted = pairings.filter(p => myTeamSet.has(p.team1) && myTeamSet.has(p.team2));
    return {
      top10: pairings.slice(0, 10),
      bottom10: [...bothDrafted].reverse().slice(0, 10),
      filtered: [],
    };
  }, [pairings, selectedTeam, myTeams]);

  return (
    <Box>
      <FormControl size="small" sx={{ mb: 2, minWidth: 160 }}>
        <InputLabel sx={{ fontSize: 13 }}>Filter by team</InputLabel>
        <Select
          value={selectedTeam}
          label="Filter by team"
          onChange={e => setSelectedTeam(e.target.value)}
          sx={{ fontSize: 13 }}
        >
          <MenuItem value=""><em>All (top / bottom 10)</em></MenuItem>
          {myTeams.map(t => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
        </Select>
      </FormControl>

      {selectedTeam ? (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {filtered.length === 0
            ? <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No pairings found for {selectedTeam}.</Typography>
            : filtered.map(p => (
              <PairingRow key={`${p.team1}|${p.team2}`} {...p} totalRosters={totalRosters} />
            ))}
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: { xs: 3, sm: 2 } }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Most Paired
            </Typography>
            {top10.map(p => (
              <PairingRow key={`${p.team1}|${p.team2}`} {...p} totalRosters={totalRosters} />
            ))}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Least Paired
            </Typography>
            {bottom10.map(p => (
              <PairingRow key={`${p.team1}|${p.team2}`} {...p} totalRosters={totalRosters} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Section tabs ──────────────────────────────────────────────────

const POSITION_STACK_ORDER = ["QB", "RB", "WR", "TE"];

const DASHBOARD_SECTIONS = [
  { id: "dash-overview",      label: "Overview" },
  { id: "dash-exposure",      label: "Exposure" },
  { id: "dash-pick-quality",  label: "Pick Quality" },
  { id: "dash-construction",  label: "Construction" },
  { id: "dash-by-team",       label: "By Team" },
];

// ── Dashboard ─────────────────────────────────────────────────────

export default function DashboardPage() {
  const { rawData, filteredRawData, setTeamFilter, providersPresent } = useDraftData();
  const { adpHistory } = useAdp();
  const { lookupForProvider, udMarketLookup, dkMarketLookup } = useMarketAdp();
  const navigate = useNavigate();
  const sc = useStatusColors();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";
  const brandAccent = surfaceTokens(isDark).brandLight;
  const brandStrong = surfaceTokens(isDark).brand;

  const hasData = rawData.length > 0;

  const [activeTab, setActiveTab] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const suppressObserver = useRef(false);

  // ── Per-pick CLV and player data ──────────────────────────────
  const { players, totalDrafts, avgClv, totalInvested, pickClvValues, clvBuckets, portfolioPlayerNames } = useMemo(() => {
    if (!rawData.length) return {
      players: [] as Array<{ name: string; position: string; team: string; exposure: number; clv: number | null; pickNumbers: number[]; rows: any[] }>,
      totalDrafts: 0, avgClv: null, totalInvested: 0,
      pickClvValues: [] as number[], clvBuckets: [] as Array<{ clv: number; count: number }>,
      portfolioPlayerNames: new Set<string>(),
    };

    const playersData = groupDataByPlayer(filteredRawData);
    const drafts = new Set(filteredRawData.map(r => r["Draft"])).size;

    let clvWeightedSum = 0, clvWeightTotal = 0, totalFee = 0;
    const seenDrafts = new Set<string>();
    const playerNames = new Set<string>();
    const allPickClvValues: number[] = [];

    const playersWithExp = playersData.map(p => {
      const playerDrafts = new Set(p.rows.map(r => r["Draft"])).size;
      const exposure = drafts > 0 ? (playerDrafts / drafts) * 100 : 0;

      // Per-pick CLV: each pick scored against its OWN platform's market, so a
      // player drafted on both UD and DK isn't scored on one board.
      let playerClvSum = 0, playerClvCount = 0;
      for (const row of p.rows) {
        const pickNum = Number(row["Pick Number"]);
        if (isNaN(pickNum)) continue;
        const rowProv = (row["Provider"] || "").trim();
        const { clv: pickClv } = computePlayerCLV(p.name, pickNum, lookupForProvider(rowProv));
        if (typeof pickClv === "number") {
          allPickClvValues.push(pickClv);
          playerClvSum += pickClv;
          playerClvCount++;
          clvWeightedSum += pickClv;
          clvWeightTotal++;
        }
      }
      const avgPickClv = playerClvCount > 0 ? playerClvSum / playerClvCount : null;

      playerNames.add(p.name);
      p.rows.forEach(r => {
        const draftId = r["Draft"];
        if (draftId && !seenDrafts.has(draftId)) {
          seenDrafts.add(draftId);
          const fee = parseFloat(r["Tournament Entry Fee"] ?? r["Draft Entry Fee"] ?? "");
          if (!isNaN(fee)) totalFee += fee;
        }
      });
      return { ...p, exposure, clv: avgPickClv };
    }).sort((a, b) => b.exposure - a.exposure);

    const bucketSize = 2;
    const bucketMap: Record<number, number> = {};
    allPickClvValues.forEach(v => {
      const bucket = Math.round(v / bucketSize) * bucketSize;
      bucketMap[bucket] = (bucketMap[bucket] || 0) + 1;
    });
    const clvBuckets = Object.entries(bucketMap)
      .map(([k, v]) => ({ clv: Number(k), count: v }))
      .sort((a, b) => a.clv - b.clv);

    return {
      players: playersWithExp,
      totalDrafts: drafts,
      avgClv: clvWeightTotal > 0 ? clvWeightedSum / clvWeightTotal : null,
      totalInvested: totalFee,
      pickClvValues: allPickClvValues,
      clvBuckets,
      portfolioPlayerNames: playerNames,
    };
  }, [rawData, filteredRawData, lookupForProvider]);

  // ── Chart data ────────────────────────────────────────────────
  const allTeams = useMemo(() => getTeamsTableFromCsv(filteredRawData), [filteredRawData]);

  // ── Slot analytics + portfolio CLV (shared by slot bands & change feed) ──
  const teamClvs = useMemo(
    () => allTeams.map(t => ({ slot: Number(t.draftSlot), clv: getTeamCLV(t, lookupForProvider(t.provider)).totalCLV })),
    [allTeams, lookupForProvider],
  );
  const portfolioClv = useMemo(
    () => (teamClvs.length && (udMarketLookup.size > 0 || dkMarketLookup.size > 0) ? teamClvs.reduce((s, t) => s + t.clv, 0) : null),
    [teamClvs, udMarketLookup, dkMarketLookup],
  );
  const slotBands = useMemo(() => slotBandClv(teamClvs), [teamClvs]);

  // ── "Since your last visit" feed ──────────────────────────────
  // Exposure keyed by NORMALIZED name (to join against marketAdpLookup),
  // plus a display-name map for rendering.
  const { exposureByNormName, displayByNormName } = useMemo(() => {
    const exp = new Map<string, number>();
    const disp = new Map<string, string>();
    for (const p of players) {
      const norm = normalizePlayerName(p.name);
      exp.set(norm, p.exposure);
      disp.set(norm, p.name);
    }
    return { exposureByNormName: exp, displayByNormName: disp };
  }, [players]);

  const [changeFeed, setChangeFeed] = useState<ChangeFeed | null>(null);
  useEffect(() => {
    // The feed attributes CLV moves to day-over-day ADP drift, which only the
    // Underdog market has (DK ADP is a static snapshot). Runs whenever the
    // portfolio contains any UD picks; dormant for a pure DraftKings portfolio.
    if (!providersPresent.includes("Underdog")) return;
    if (!hasData || portfolioClv === null || udMarketLookup.size === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      const raw = localStorage.getItem(VISIT_SNAPSHOT_KEY);
      const snap: VisitSnapshot | null = raw ? JSON.parse(raw) : null;
      if (snap && snap.date < today && snap.adps && typeof snap.portfolioClv === "number") {
        setChangeFeed(buildChangeFeed(snap, portfolioClv, udMarketLookup, exposureByNormName));
      }
      // Write a fresh baseline only when the day rolls over (or first visit),
      // so refreshing within the same day keeps comparing against yesterday.
      if (!snap || snap.date < today) {
        const adps: Record<string, number> = {};
        udMarketLookup.forEach((v, k) => { adps[k] = v; });
        localStorage.setItem(VISIT_SNAPSHOT_KEY, JSON.stringify({ date: today, portfolioClv, adps }));
      }
    } catch {
      // localStorage unavailable/corrupt — feed simply doesn't render.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData, portfolioClv, udMarketLookup, exposureByNormName, providersPresent]);
  const chartLinePositional = useMemo(() => getLineChartDataForPositionByRound(filteredRawData), [filteredRawData]);
  const uniquePlayersByTeam = useMemo(() => getUniquePlayersByTeam(filteredRawData), [filteredRawData]);
  const chartTeamPositionBreakdown = useMemo(() => {
    const raw = getStackedTeamPositionChartData(filteredRawData);
    return {
      ...raw,
      chartData: [...raw.chartData].sort((a, b) => {
        const aTotal = Object.values(a).reduce((s: number, v) => typeof v === "number" ? s + v : s, 0);
        const bTotal = Object.values(b).reduce((s: number, v) => typeof v === "number" ? s + v : s, 0);
        return (bTotal as number) - (aTotal as number);
      }),
    };
  }, [filteredRawData]);
  const draftSlotHistogramData = useMemo(() => getDraftSlotHistogram(filteredRawData), [filteredRawData]);
  const teamStructureHistogramData = useMemo(() => getTeamStructureHistogram(allTeams), [allTeams]);
  const teamPairingData = useMemo(() => getTeamPairingFrequencies(allTeams), [allTeams]);

  const avgSlot = useMemo(() => {
    if (!draftSlotHistogramData.length) return null;
    const total = draftSlotHistogramData.reduce((s, d) => s + Number(d.slot) * d.count, 0);
    const count = draftSlotHistogramData.reduce((s, d) => s + d.count, 0);
    return count > 0 ? total / count : null;
  }, [draftSlotHistogramData]);

  const topStructure = useMemo(() =>
    teamStructureHistogramData.length > 0
      ? [...teamStructureHistogramData].sort((a, b) => b.count - a.count)[0].structure
      : null,
    [teamStructureHistogramData]);

  const recentDrafts = useMemo(() => {
    if (!allTeams.length) return [];

    // Build a cache of date → historical ADP lookup from adpHistory
    const historicalLookupCache = new Map<string, Map<string, number>>();
    function getHistoricalLookup(dateStr: string): Map<string, number> {
      if (historicalLookupCache.has(dateStr)) return historicalLookupCache.get(dateStr)!;
      const lookup = new Map<string, number>();
      for (const p of adpHistory) {
        const relevant = p.history.filter(h => h.date <= dateStr);
        if (relevant.length > 0) lookup.set(normalizePlayerName(p.name), relevant[relevant.length - 1].adp);
      }
      historicalLookupCache.set(dateStr, lookup);
      return lookup;
    }

    // Compute current CLV + draft-date CLV for all teams in one pass
    const allTeamData = allTeams.map(team => {
      const clvResult = getTeamCLV(team, lookupForProvider(team.provider));
      const qb = clvResult.playerCLVs.filter(p => p.position === "QB").length;
      const rb = clvResult.playerCLVs.filter(p => p.position === "RB").length;
      const wr = clvResult.playerCLVs.filter(p => p.position === "WR").length;
      const te = clvResult.playerCLVs.filter(p => p.position === "TE").length;
      const structure = `${qb}QB-${rb}RB-${wr}WR-${te}TE`;
      const d = new Date(team.date);
      const formattedDate = isNaN(d.getTime()) ? team.date :
        d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

      // Draft-date CLV using the historical UD ADP snapshot. Only meaningful for
      // Underdog teams — the dated history is UD-only, so DK teams get no draft-day CLV.
      let draftClv: number | null = null;
      if (team.date && adpHistory.length > 0 && team.provider !== "DraftKings") {
        const draftLookup = getHistoricalLookup(team.date);
        if (draftLookup.size > 0) draftClv = getTeamCLV(team, draftLookup).totalCLV;
      }

      return { team, clv: clvResult.totalCLV, draftClv, structure, formattedDate };
    });

    // Rank by current CLV descending
    const sorted = [...allTeamData].sort((a, b) => b.clv - a.clv);
    const rankMap = new Map(sorted.map((t, i) => [t.team.draftId, i + 1]));
    const total = allTeams.length;

    return [...allTeamData]
      .filter(t => t.team.date)
      .sort((a, b) => new Date(b.team.date).getTime() - new Date(a.team.date).getTime())
      .slice(0, 8)
      .map(entry => ({ ...entry, rank: rankMap.get(entry.team.draftId) ?? total, total }));
  }, [allTeams, lookupForProvider, adpHistory]);

  // ── Intersection observer for tab sync ────────────────────────
  useEffect(() => {
    if (!hasData) return;
    const observers = sectionRefs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting && !suppressObserver.current) setActiveTab(i); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, [hasData]);

  const scrollToSection = (i: number) => {
    suppressObserver.current = true;
    setActiveTab(i);
    sectionRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => { suppressObserver.current = false; }, 800);
  };

  // ── Download helpers ──────────────────────────────────────────
  const downloadRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const makeDownload = useCallback((key: string, filename: string) => async () => {
    const el = downloadRefs.current[key];
    if (!el) return;
    try {
      const dataUrl = await toPng(el, { backgroundColor: CHART_EXPORT_BG });
      const link = document.createElement("a");
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Export failed", err);
    }
  }, []);

  if (!hasData) return <HeroEmptyState />;

  const axisStyle = AXIS_TICK;
  const yAxisWidth = isMobile ? 28 : 38;
  const lowerRowChartHeight = isMobile ? 300 : 280;

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>

      {/* Sticky tab bar */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 10, bgcolor: "background.paper",
        borderBottom: 1, borderColor: "divider",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => scrollToSection(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, "& .MuiTab-root": { minHeight: 44, fontSize: 13, py: 0 } }}
        >
          {DASHBOARD_SECTIONS.map(s => (
            <Tab key={s.id} label={s.label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: "flex", flexDirection: "column", gap: 4 }}>

        {/* ── Since your last visit ─────────────────────────── */}
        {changeFeed && (changeFeed.movers.length > 0 || Math.abs(changeFeed.clvDelta) >= 0.1) && (
          <Paper variant="outlined" sx={{
            borderRadius: 2, px: 2, py: 1.25, mb: -2,
            display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2.5, rowGap: 0.75,
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary", flexShrink: 0 }}>
              Since {new Date(changeFeed.sinceDate + "T12:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </Typography>
            {Math.abs(changeFeed.clvDelta) >= 0.1 && (
              <Typography sx={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>
                Portfolio CLV{" "}
                <Box component="span" sx={{ fontWeight: 700, color: changeFeed.clvDelta >= 0 ? sc.good : sc.bad }}>
                  {changeFeed.clvDelta >= 0 ? "+" : "−"}{Math.abs(changeFeed.clvDelta).toFixed(1)}
                </Box>
              </Typography>
            )}
            {changeFeed.movers.map(m => {
              const display = displayByNormName.get(m.name) ?? m.name;
              const tone = m.delta >= 0 ? sc.good : sc.bad;
              return (
                <Typography
                  key={m.name}
                  onClick={() => navigate(`/players?q=${encodeURIComponent(display)}`)}
                  sx={{
                    fontSize: 12.5, cursor: "pointer", fontVariantNumeric: "tabular-nums",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>{display}</Box>{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: tone }}>
                    {m.delta >= 0 ? "▲ +" : "▼ −"}{Math.abs(m.delta).toFixed(1)}
                  </Box>{" "}
                  <Box component="span" sx={{ color: "text.disabled", fontSize: 11 }}>({Math.round(m.exposure)}% owned)</Box>
                </Typography>
              );
            })}
          </Paper>
        )}

        {/* KPI strip */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(auto-fit, minmax(150px, 1fr))",
          },
          gap: { xs: 1, md: 1.5 },
        }}>
          <StatCard metric={players.length} label="Unique Players" />
          <StatCard metric={totalDrafts} label="Drafts" />
          <StatCard
            metric={avgClv !== null ? (avgClv > 0 ? "+" : "") + avgClv.toFixed(1) : "—"}
            label="Avg CLV / pick"
            accentColor={avgClv !== null && avgClv > 0 ? sc.good : sc.bad}
          />
          {totalInvested > 0 && (
            <StatCard metric={`$${totalInvested.toFixed(0)}`} label="Total Invested" />
          )}
        </Box>

        {/* ── Section 1: Overview ───────────────────────────── */}
        <Box ref={el => { sectionRefs.current[0] = el as HTMLDivElement | null; }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>

            <Widget title="Recent Drafts" accent={brandAccent}
              description="Your 8 most recent drafts. The dumbbell tracks each build's CLV from draft day (hollow) to live (solid). The connector is the market drifting your way, or away."
              headerRight={
                <IconButton size="small" onClick={makeDownload("recent-drafts", "recent-drafts.png")} title="Download" aria-label="Download chart as image">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box ref={el => { downloadRefs.current["recent-drafts"] = el as HTMLDivElement | null; }}
                sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {recentDrafts.map(({ team, clv, draftClv, structure, formattedDate, rank, total }) => {
                  const liveTone = clv >= 0 ? sc.good : sc.bad;
                  const drift = draftClv !== null ? clv - draftClv : null;
                  const driftTone = drift === null ? sc.neutral : drift >= 0 ? sc.good : sc.bad;
                  // Shared CLV scale across the visible drafts so the dumbbells compare.
                  const clvVals = recentDrafts.flatMap(d => d.draftClv !== null ? [d.clv, d.draftClv] : [d.clv]);
                  const clvLo = Math.min(0, ...clvVals);
                  const clvSpan = (Math.max(0, ...clvVals) - clvLo) || 1;
                  const clvPos = (v: number) => `${((v - clvLo) / clvSpan) * 100}%`;
                  return (
                    <Box key={team.draftId} sx={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      py: 0.875, px: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider", gap: 1.5,
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {team.tournament || "Draft"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          {formattedDate} · {structure}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1.25, flexShrink: 0, alignItems: "center" }}>
                        {draftClv !== null && drift !== null && (
                          <Box
                            sx={{ position: "relative", width: 116, height: 16, flexShrink: 0 }}
                            title={`Draft ${draftClv >= 0 ? "+" : ""}${draftClv.toFixed(1)} → live ${clv >= 0 ? "+" : ""}${clv.toFixed(1)}`}
                          >
                            <Box sx={{ position: "absolute", top: 7, left: 0, right: 0, height: 2, bgcolor: "divider", borderRadius: 1 }} />
                            {/* drift connector */}
                            <Box sx={{ position: "absolute", top: 7, height: 2, borderRadius: 1, bgcolor: driftTone, opacity: 0.45,
                              left: clvPos(Math.min(clv, draftClv)), width: `${(Math.abs(drift) / clvSpan) * 100}%` }} />
                            {/* draft-day (hollow) */}
                            <Box sx={{ position: "absolute", top: 3, width: 10, height: 10, borderRadius: "50%",
                              border: "2px solid", borderColor: driftTone, bgcolor: "background.paper",
                              left: clvPos(draftClv), transform: "translateX(-50%)" }} />
                            {/* live (solid) */}
                            <Box sx={{ position: "absolute", top: 2, width: 12, height: 12, borderRadius: "50%", bgcolor: liveTone,
                              left: clvPos(clv), transform: "translateX(-50%)",
                              boxShadow: (t) => `0 0 0 2px ${t.palette.background.paper}` }} />
                          </Box>
                        )}
                        <Box sx={{ textAlign: "right", minWidth: 54 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: liveTone, fontVariantNumeric: "tabular-nums" }}>
                            {clv >= 0 ? "+" : ""}{clv.toFixed(1)}
                          </Typography>
                          <Typography sx={{ fontSize: 9, color: "text.disabled", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {drift !== null ? `${drift >= 0 ? "▲ +" : "▼ −"}${Math.abs(drift).toFixed(1)} · ` : ""}#{rank}/{total}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                {recentDrafts.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: "text.disabled", py: 2, textAlign: "center" }}>
                    No draft data yet.
                  </Typography>
                )}
              </Box>
            </Widget>

            <Widget title="ADP Movers" accent={sc.warn}
              description="7-day ADP moves, ranked by portfolio impact (move × your exposure). A small move you're heavy in outranks a big move you own once."
              headerRight={
                <IconButton size="small" onClick={makeDownload("adp-movers", "adp-movers.png")} title="Download" aria-label="Download chart as image">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box ref={el => { downloadRefs.current["adp-movers"] = el as HTMLDivElement | null; }}>
                <AdpMovers
                  adpHistory={adpHistory}
                  playerNames={portfolioPlayerNames}
                  exposureByName={new Map(players.map(p => [p.name, p.exposure]))}
                  onSelect={name => navigate(`/players?q=${encodeURIComponent(name)}`)}
                />
              </Box>
            </Widget>
          </Box>
        </Box>

        {/* ── Section 2: Exposure ───────────────────────────── */}
        <Box ref={el => { sectionRefs.current[1] = el as HTMLDivElement | null; }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>

            <Widget title="Exposure Heatmap" accent={POSITION_COLORS.WR}
              description="Your most-drafted players, by the share of drafts they appear in. The tick marks 8.3%, the average share in a 12-team draft."
              headerRight={
                <IconButton size="small" onClick={makeDownload("exposure-heatmap", "exposure-heatmap.png")} title="Download" aria-label="Download chart as image">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box ref={el => { downloadRefs.current["exposure-heatmap"] = el as HTMLDivElement | null; }}>
                <ExposureHeatmap
                  players={players.slice(0, 15).map(p => ({ name: p.name, exposure: p.exposure, position: p.position, team: p.team }))}
                  onNavigate={name => navigate(`/players?q=${encodeURIComponent(name)}`)}
                />
              </Box>
            </Widget>

            <Widget title="Exposure vs. Value" accent={POSITION_COLORS.WR}
              description="Each dot is a player; bigger dots mean you've drafted them more. Up and to the right is high exposure at good value. Down and to the right is high exposure at a reach."
              headerRight={
                <IconButton size="small" onClick={makeDownload("exposure-vs-value", "exposure-vs-value.png")} title="Download" aria-label="Download chart as image">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box ref={el => { downloadRefs.current["exposure-vs-value"] = el as HTMLDivElement | null; }}>
                <ExposureVsValue players={players} onNavigate={name => navigate(name ? `/players?q=${encodeURIComponent(name)}` : "/players")} />
              </Box>
            </Widget>
          </Box>
        </Box>

        {/* ── Section 3: Pick Quality ───────────────────────── */}
        <Box ref={el => { sectionRefs.current[2] = el as HTMLDivElement | null; }}>
          <Widget title="CLV Distribution" accent={sc.good}
            headerRight={
              <IconButton size="small" onClick={makeDownload("clv-distribution", "clv-distribution.png")} title="Download" aria-label="Download chart as image">
                <DownloadIcon fontSize="small" />
              </IconButton>
            }
          >
            <Box ref={el => { downloadRefs.current["clv-distribution"] = el as HTMLDivElement | null; }}>
              <ClvDistribution pickClvValues={pickClvValues} clvBuckets={clvBuckets} />
            </Box>
          </Widget>
        </Box>

        {/* ── Section 4: Construction ───────────────────────── */}
        <Box ref={el => { sectionRefs.current[3] = el as HTMLDivElement | null; }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Widget title="Positional % by Round" accent={POSITION_COLORS.WR}
              description="The share of your picks spent on each position in each round, across all your drafts."
            >
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={chartLinePositional.data}>
                  <XAxis dataKey="round" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis width={yAxisWidth} tick={axisStyle} axisLine={false} tickLine={false} />
                  <RechartsTooltip content={<DarkTooltip />} />
                  {chartLinePositional.positions.map(pos => (
                    <Area
                      key={pos}
                      type="monotone"
                      dataKey={pos}
                      stroke={POSITION_COLORS[pos] || "#888"}
                      fill={POSITION_COLORS[pos] || "#888"}
                      fillOpacity={0.1}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                  <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12 }}
                    formatter={val => <span style={{ color: POSITION_COLORS[val] || "#888" }}>{val}</span>} />
                </AreaChart>
              </ResponsiveContainer>
            </Widget>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, alignItems: "stretch" }}>

              <Widget title="Draft Slot Distribution" accent={POSITION_COLORS.TE}
                description={avgSlot !== null
                  ? `Which draft slots you pick from. Your average slot is ${avgSlot.toFixed(1)}, and the bands below show your average CLV from each part of the board.`
                  : "Which draft slots you pick from."}
              >
                <ResponsiveContainer width="100%" height={lowerRowChartHeight}>
                  <BarChart data={draftSlotHistogramData} barCategoryGap="20%">
                    <XAxis dataKey="slot" tick={axisStyle} axisLine={false} tickLine={false} />
                    <YAxis width={yAxisWidth} allowDecimals={false} tick={axisStyle} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<DarkTooltip formatter={(v: number) => [v, "Drafts"]} />} />
                    {avgSlot !== null && (
                      <ReferenceLine x={String(Math.round(avgSlot))} stroke={sc.warn}
                        strokeDasharray="4 4" label={{ value: "Avg", fill: sc.warn, fontSize: 11 }} />
                    )}
                    <Bar dataKey="count" fill={POSITION_COLORS.TE} radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="count" position="top"
                        style={{ fontSize: 11, fill: isDark ? "#aaa" : "#555" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Where you draft best: avg CLV per slot band */}
                {slotBands.some(b => b.count > 0) && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mt: 1.5 }}>
                    {slotBands.map(b => (
                      <Box key={b.label} sx={{
                        textAlign: "center", border: "1px solid", borderColor: "divider",
                        borderRadius: 1.5, py: 0.75, px: 0.5,
                      }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "text.secondary" }}>
                          {b.label}
                        </Typography>
                        <Typography sx={{
                          fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.3,
                          color: b.count === 0 ? "text.disabled" : b.avgClv >= 0 ? sc.good : sc.bad,
                        }}>
                          {b.count === 0 ? "—" : `${b.avgClv >= 0 ? "+" : "−"}${Math.abs(b.avgClv).toFixed(1)}`}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "text.disabled" }}>
                          {b.count === 0 ? "no drafts" : `avg CLV · ${b.count} ${b.count === 1 ? "draft" : "drafts"}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Widget>

              <Widget title="Team Structure" accent={brandStrong}
                description={topStructure
                  ? `How many QB/RB/WR/TE you roster per team. Your most common build is ${topStructure}.`
                  : "How many QB/RB/WR/TE you roster per team."}
              >
                <Box sx={{ width: "100%", overflow: "hidden" }}>
                  <ResponsiveContainer width="100%" height={lowerRowChartHeight}>
                    <BarChart data={teamStructureHistogramData} barCategoryGap={isMobile ? "28%" : "20%"}>
                      <XAxis
                        dataKey="structure"
                        tick={{ ...axisStyle, fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        angle={isMobile ? 0 : -35}
                        textAnchor={isMobile ? "middle" : "end"}
                        height={isMobile ? 32 : 60}
                        interval={isMobile ? "preserveStartEnd" : 0}
                      />
                      <YAxis width={yAxisWidth} allowDecimals={false} tick={axisStyle} axisLine={false} tickLine={false} />
                      <RechartsTooltip content={<DarkTooltip formatter={(v: number, _: string, props: any) =>
                        [`${v} (${props?.payload?.percent ?? ""}%)`, "Teams"]} />} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={isMobile ? 24 : 40}>
                        {teamStructureHistogramData.map(entry => (
                          <Cell key={entry.structure}
                            fill={entry.structure === topStructure ? brandStrong : brandAccent}
                            opacity={entry.structure === topStructure ? 1 : 0.55}
                          />
                        ))}
                        <LabelList dataKey="percent" position="top"
                          formatter={(v: unknown) => `${v}%`}
                          style={{ fontSize: 10, fill: isDark ? "#aaa" : "#555" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Widget>
            </Box>
          </Box>
        </Box>

        {/* ── Section 5: By Team ────────────────────────────── */}
        <Box ref={el => { sectionRefs.current[4] = el as HTMLDivElement | null; }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Widget title="Unique Players by Team" accent={POSITION_COLORS.RB}
              description="How many different players you've drafted from each NFL team. Click a bar to see them."
            >
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={uniquePlayersByTeam} barCategoryGap="20%">
                  <XAxis dataKey="team" tick={{ ...axisStyle, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis width={yAxisWidth} tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
                  <RechartsTooltip content={<UniquePlayersTooltip />} />
                  <Bar dataKey="uniquePlayers" radius={[4, 4, 0, 0]}
                    style={{ cursor: "pointer" }}
                    onClick={(data: any) => { setTeamFilter(data.team); navigate("/players"); }}>
                    {uniquePlayersByTeam.map(entry => (
                      <Cell key={entry.team} fill={teamColorBg(TEAM_COLORS[entry.team] || semantic.neutral, isDark)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Widget>

            <Widget title="Team × Position Breakdown" accent={POSITION_COLORS.QB}
              description="Your picks from each NFL team, broken down by position."
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartTeamPositionBreakdown.chartData} barCategoryGap="20%">
                  <XAxis dataKey="team" tick={{ ...axisStyle, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis width={yAxisWidth} tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
                  <RechartsTooltip content={<DarkTooltip />} />
                  {POSITION_STACK_ORDER.filter(pos => chartTeamPositionBreakdown.positions.includes(pos)).map((pos, i, arr) => (
                    <Bar key={pos} dataKey={pos} stackId="a"
                      fill={POSITION_COLORS[pos] || "#888"}
                      radius={i === arr.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                  ))}
                  <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12 }}
                    formatter={val => <span style={{ color: POSITION_COLORS[val] || "#888" }}>{val}</span>} />
                </BarChart>
              </ResponsiveContainer>
            </Widget>

            <Widget title="Team Pairing Frequency" accent={POSITION_COLORS.RB}
              description="How often you draft players from two NFL teams on the same roster. Use the dropdown to filter to one team."
            >
              <TeamPairingWidget
                pairings={teamPairingData.pairings}
                myTeams={teamPairingData.myTeams}
                totalRosters={teamPairingData.totalRosters}
              />
            </Widget>
          </Box>
        </Box>

      </Box>
    </Box>
  );
}
